import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import NotificationContainer from 'react-notifications/lib/NotificationContainer';

import { useModal } from '../context/ModalContext';
import LastNameConfirmationForm from '../forms/LastNameConfirmationForm';
import Text from '../components/common/Text';
import HotelLogo from '../components/common/HotelLogo';
import Footer from '../components/common/Footer';
import LoadingSpinner from '../components/common/LoadingSpinner';
import RoomNumberLabel from '../components/RoomNumberLabel';
import Button from '../components/common/Button';
import HorizontalDivider from '../components/common/HorizontalDivider';
import {
  checkReservation,
  getHotelInformation,
  getRoomInformation,
} from '../redux/reducer/actions';
import { setUrls } from '../redux/reducer/slice';

const StartPage = () => {
  const { uniqueId, propertyId, selectedItemId } = useParams();
  const { openModal, closeModal } = useModal();
  const dispatch = useDispatch();
  const hotelInformation = useSelector((state) => state.app?.hotelInformation);
  const roomInformation = useSelector((state) => state.app?.roomInformation);
  const urls = useSelector((state) => state.app?.urls || {});
  const { genericStartPageUrl, directoryPageUrl } = urls;
  const navigate = useNavigate();
  const roomLabel = roomInformation && Array.isArray(roomInformation)
    ? roomInformation[0].name
    : '---';

  useEffect(() => {
    if (uniqueId && propertyId && selectedItemId) {
      dispatch(setUrls({ uniqueId, propertyId, selectedItemId }));
    }
  }, [dispatch, uniqueId, propertyId, selectedItemId]);

  useEffect(() => {
    // TODO: Session will be implemented later
    // dispatch(initiateSession());
    dispatch(getHotelInformation({ uniqueId, hotelId: propertyId }));
    dispatch(
      getRoomInformation({ hotelId: propertyId, roomId: selectedItemId }),
    );
  }, [dispatch, uniqueId, propertyId, selectedItemId]);

  if (hotelInformation !== undefined && !hotelInformation && !hotelInformation.logo && !hotelInformation.color) {
    return (
      <main className="flex min-h-screen flex-col gap-16 py-16">
        <section className="mt-20 text-center">
          <LoadingSpinner message="Loading hotel information" dark />
        </section>
        <div className="mt-auto">
          <Footer />
        </div>
      </main>
    );
  }

  const wrongLastNameModal = {
    title: 'Invalid last name',
    body: (
      <div className="flex flex-col gap-6">
        <div className="py-3">
          <Text>
            <span>Please enter the correct last name under which the reservation to </span>
            <span className="font-bold"> Room </span>
            <span className="font-bold">{roomLabel}</span>
            <span> was made.</span>
          </Text>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            variant="primary"
            text="Retry"
            onClick={closeModal}
          />
          <Button
            variant="secondary"
            text="Select another room"
            onClick={() => {
              closeModal();
              navigate(genericStartPageUrl);
            }}
          />
        </div>
      </div>
    ),
  };

  const reservationNotFoundModal = {
    title: 'Reservation not found',
    body: (
      <div className="flex flex-col gap-6">
        <div className="py-3">
          <Text>Please contact reception desk.</Text>
        </div>
      </div>
    ),
  };

  return (
    <main className="flex min-h-screen flex-col gap-16 py-16">
      <section className="flex flex-col items-center gap-12">
        <HotelLogo />
        <h1 className="my-6 max-w-md text-center font-nunito text-7xl">
          <span>Good to see </span>
          <span className="text-pj-primary-400">YOU</span>
        </h1>
        <Text center>
          To confirm your identity, kindly enter the last name used to make the
          reservation for this room. If this is not your room number, please
          contact the reception.
        </Text>
      </section>
      <div className="flex flex-col gap-8">
        <RoomNumberLabel roomNumber={roomLabel} />
        <HorizontalDivider />
        <LastNameConfirmationForm
          onSubmit={async (value) => {
            const response = await dispatch(
              checkReservation({
                uniqueId,
                hotelId: propertyId,
                roomId: selectedItemId,
                lastname: value.lastName.toString(),
              }),
            );
            if (response.type.includes('rejected')) {
              if (response.payload.includes('401')) {
                openModal(wrongLastNameModal);
              } else {
                openModal(reservationNotFoundModal);
              }
            } else {
              navigate(directoryPageUrl);
            }
          }}
        />
      </div>
      <div className="mt-auto">
        <Footer />
      </div>
      <NotificationContainer />
    </main>
  );
};
export default StartPage;
